import React, { useMemo } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    getFallbackAccountRoleForLineItem,
    LineItemFallbackAccountType,
} from "@common/helpers/accounting";
import {
    findEnabledAccountByRole,
    getDefaultAccrualAccountId,
} from "@common/helpers/accrualAccount";
import {
    getEffectiveAccrualLedgerAccountId,
    getEffectiveGeneralLedgerAccountId,
} from "@common/helpers/transactions";
import { useAccounts } from "@hooks/useAccounts";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useHasAccrualLedger } from "@api/ledger.api";
import { useHasMatchableDocuments } from "@api/accrual-review.api";
import { MatchItemType, MatchableItem, MatchableTransaction } from "./types";
import { transactionToMatchableItem } from "./mappers";
import { TransactionMatchFooter } from "./TransactionMatchFooter";
import { useAccrualMatch } from "./useAccrualMatch";
import { SearchMatchesSection } from "./SearchMatchesSection";
import { useMatchesPanelExtraHeightVariable } from "./useMatchesPanelResize";
import {
    getAccrualMatchDrawerDefaults,
    getSearchMatchTypeOptions,
} from "./matchDrawerDefaults";
import { MatchScheduleColumnProvider } from "./schedule/matchScheduleColumn.context";
import styles from "./AccrualMatchPage.module.scss";

interface Props {
    transaction: MatchableTransaction;
}

export const AccrualMatchPage: React.FC<Props> = ({ transaction }) => {
    const { selectedItems, isCashBasis } = useAccrualMatch();
    const { hasAccess: hasApReconciliation } = useAllowFeature(
        PREMIUM_FEATURES.AP_RECONCILIATION,
    );

    const entityIdForGLAccount = useEntityIdForGLAccount(transaction);
    const hasAccrualLedger = useHasAccrualLedger(entityIdForGLAccount);
    const { accounts } = useAccounts({
        entityId: entityIdForGLAccount,
    });

    const defaultAccrualAccountId = useMemo(
        () =>
            getDefaultAccrualAccountId({
                transaction,
                accounts,
            }),
        [transaction, accounts],
    );

    const sourceAccountIds = useMemo(() => {
        if (selectedItems.length === 0) {
            return defaultAccrualAccountId ? [defaultAccrualAccountId] : [];
        }

        const ids = [
            ...new Set(
                selectedItems.flatMap((item) => {
                    if (item.accountIds.length > 0) {
                        return item.accountIds;
                    }

                    const fallbackType =
                        item.type === MatchItemType.INVOICE
                            ? LineItemFallbackAccountType.invoiceAccountsReceivable
                            : LineItemFallbackAccountType.billAccountsPayable;
                    const role = getFallbackAccountRoleForLineItem({
                        lineItemFallbackAccountType: fallbackType,
                    });
                    const fallbackId = findEnabledAccountByRole(
                        accounts,
                        role,
                    )?.id;
                    return fallbackId ? [fallbackId] : [];
                }),
            ),
        ];

        return ids;
    }, [selectedItems, defaultAccrualAccountId, accounts]);

    const sourceItem = useMemo<MatchableItem>(() => {
        // Cash-only entities show the source transaction's cash ledger
        // account; AR/AP aggregation across selected docs needs an accrual
        // ledger.
        if (isCashBasis) {
            return transactionToMatchableItem({
                transaction,
                isCashBasis,
                // Same entity the `accounts` list and `journalAccount` below
                // resolve against, so the source row's Account column can't
                // disagree with the tab the drawer opens on.
                entityIdForGLAccount,
            });
        }
        const item = transactionToMatchableItem({
            transaction,
            accrualAccountId: defaultAccrualAccountId,
            entityIdForGLAccount,
        });
        // Mirrors the `accountIds` override below: the source row's Accrual
        // ledger column (PRO-12048) must show the same AR/AP aggregation
        // across selected docs as the legacy Account column, not just the
        // transaction's own accrual account.
        return {
            ...item,
            accountIds: sourceAccountIds,
            accrualAccountIds: sourceAccountIds,
        };
    }, [
        transaction,
        isCashBasis,
        entityIdForGLAccount,
        defaultAccrualAccountId,
        sourceAccountIds,
    ]);

    // The journal-entry fallback filters entries by ledger entity, so it has
    // to use the same entity the `accounts` list above was fetched for —
    // `useEntityIdForGLAccount` resolves non-business transactions to their
    // financial account's business entity, which is not `transaction.entity`.
    const journalAccount = useMemo(() => {
        if (entityIdForGLAccount == null) {
            return null;
        }

        const cashJournalAccountId = getEffectiveGeneralLedgerAccountId({
            amount: transaction.amount,
            accountId: transaction.accountId ?? null,
            entityId: entityIdForGLAccount,
            journalEntries: transaction.journalEntries,
        });
        const journalAccountId = isCashBasis
            ? cashJournalAccountId
            : (getEffectiveAccrualLedgerAccountId({
                  amount: transaction.amount,
                  accrualAccountId: transaction.accrualAccountId ?? null,
                  entityId: entityIdForGLAccount,
                  journalEntries: transaction.journalEntries,
              }) ?? cashJournalAccountId);

        if (!journalAccountId) {
            return null;
        }

        return (
            accounts.find((account) => account.id === journalAccountId) ?? null
        );
    }, [accounts, entityIdForGLAccount, isCashBasis, transaction]);

    // Cash-basis document matching only accepts documents owned by
    // `transaction.entityId`, while the chart of accounts driving the default
    // tab belongs to `entityIdForGLAccount`. When those differ (a personal
    // transaction sitting in a business account) no document of the pinned
    // entity can ever match, so the document tabs are not offered at all.
    const canMatchDocuments =
        !isCashBasis || entityIdForGLAccount === transaction.entity.id;

    // No point querying existence of invoices/bills for a transaction that
    // can never match a document at all (see `canMatchDocuments` above). The
    // hook stays permissive (both `true`) while the check is in flight or if it
    // fails, and only narrows once a completed check reports zero documents —
    // avoiding the tab options flashing as the match drawer re-opens and never
    // hiding a tab on an unresolved/failed probe.
    const { hasInvoices, hasBills } =
        useHasMatchableDocuments(canMatchDocuments);

    // Derived once here and passed down: the child renders the type selector
    // from it and the default type is clamped to it, so the two can't drift.
    const searchMatchTypeOptions = useMemo(
        () =>
            getSearchMatchTypeOptions({
                transactionAmount: transaction.amount,
                hasApReconciliation,
                isCashBasis,
                canMatchDocuments,
                hasInvoices,
                hasBills,
            }),
        [
            transaction.amount,
            hasApReconciliation,
            isCashBasis,
            canMatchDocuments,
            hasInvoices,
            hasBills,
        ],
    );

    const drawerDefaults = useMemo(
        () =>
            getAccrualMatchDrawerDefaults({
                journalAccount,
                transactionAmount: transaction.amount,
                searchMatchTypeOptions,
            }),
        [journalAccount, transaction.amount, searchMatchTypeOptions],
    );

    const { sectionRef, setMatchesPanelExtraHeight } =
        useMatchesPanelExtraHeightVariable();

    return (
        <MatchScheduleColumnProvider
            sourceItem={sourceItem}
            selectedItems={selectedItems}
        >
            <div className={styles.page}>
                <div className={styles.subtitleBar}>
                    {hasAccrualLedger ? (
                        <>
                            <span>Step 1 of 2</span>
                            <span>
                                Find and confirm matches or resolve the
                                difference
                            </span>
                        </>
                    ) : (
                        <span>Find and confirm match</span>
                    )}
                </div>

                <div className={styles.searchSection} ref={sectionRef}>
                    <SearchMatchesSection
                        key={transaction.id}
                        defaultType={drawerDefaults.defaultType}
                        defaultSearchMode={drawerDefaults.defaultSearchMode}
                        searchMatchTypeOptions={searchMatchTypeOptions}
                        renderFooter={(searchFooterState) => (
                            <TransactionMatchFooter
                                transaction={transaction}
                                sourceItem={sourceItem}
                                searchFooterState={searchFooterState}
                                hasAccrualLedger={hasAccrualLedger}
                                onMatchesPanelExtraHeightChange={
                                    setMatchesPanelExtraHeight
                                }
                            />
                        )}
                    />
                </div>
            </div>
        </MatchScheduleColumnProvider>
    );
};
